import { useState, useEffect } from 'react';
import { Loader2, Bell, Send, X, Mail, ToggleLeft, ToggleRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const NotificationsTab = ({ headers }) => {
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [emailInput, setEmailInput] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on mount only
  useEffect(() => {
    (async () => {
      try {
        const [sRes, hRes] = await Promise.all([
          fetch(`${API}/api/notifications/settings`, { headers, credentials: 'include' }),
          fetch(`${API}/api/notifications/history`, { headers, credentials: 'include' }),
        ]);
        if (sRes.ok) setSettings(await sRes.json());
        if (hRes.ok) { const d = await hRes.json(); setHistory(d.notifications || []); }
      } catch (e) { console.error('Failed to fetch notifications:', e); }
      finally { setLoading(false); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on mount only
  }, []);

  const save = async (updates) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/notifications/settings`, {
        method: 'PUT', headers, credentials: 'include', body: JSON.stringify(updates),
      });
      if (res.ok) setSettings(await res.json());
    } catch (e) { console.error('Failed to save notification settings:', e); }
    finally { setSaving(false); }
  };

  const toggle = (key) => save({ [key]: !settings[key] });

  const addEmail = () => {
    if (!emailInput.trim() || !emailInput.includes('@')) return;
    const updated = [...(settings.recipient_emails || []), emailInput.trim()];
    save({ recipient_emails: updated });
    setEmailInput('');
  };

  const removeEmail = (email) => {
    const updated = (settings.recipient_emails || []).filter(e => e !== email);
    save({ recipient_emails: updated });
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await fetch(`${API}/api/notifications/test`, { method: 'POST', headers, credentials: 'include' });
    } catch (e) { console.error('Failed to send test notification:', e); }
    finally { setTesting(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-taxera-blue" /></div>;
  if (!settings) return <p className="text-sm text-slate-400 text-center py-12">Unable to load notification settings.</p>;

  const ToggleRow = ({ label, desc, settingKey }) => (
    <div className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0">
      <div>
        <div className="text-sm font-medium text-taxera-charcoal">{label}</div>
        <div className="text-xs text-slate-400">{desc}</div>
      </div>
      <button onClick={() => toggle(settingKey)} className="focus:outline-none" data-testid={`toggle-${settingKey}`}>
        {settings[settingKey] ? (
          <ToggleRight className="w-8 h-8 text-taxera-teal" />
        ) : (
          <ToggleLeft className="w-8 h-8 text-slate-300" />
        )}
      </button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-notifications">
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${settings.enabled ? 'bg-taxera-teal/10' : 'bg-slate-100'}`}>
              <Bell className={`w-4 h-4 ${settings.enabled ? 'text-taxera-teal' : 'text-slate-400'}`} />
            </div>
            <div>
              <h3 className="text-sm font-bold text-taxera-charcoal">Email Notifications</h3>
              <p className="text-xs text-slate-400">{settings.enabled ? 'Active — emails will be sent' : 'Paused — no emails will be sent'}</p>
            </div>
          </div>
          <button onClick={() => toggle('enabled')} className="focus:outline-none" data-testid="toggle-master">
            {settings.enabled ? <ToggleRight className="w-10 h-10 text-taxera-teal" /> : <ToggleLeft className="w-10 h-10 text-slate-300" />}
          </button>
        </div>
        <button onClick={sendTest} disabled={testing || !settings.enabled} className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-600 rounded-full text-sm font-semibold hover:bg-slate-200 transition-all disabled:opacity-50" data-testid="send-test-btn">
          {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
          Send Test Email
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="text-sm font-bold text-taxera-charcoal mb-3">Recipient Emails</h3>
        <div className="flex flex-wrap gap-2 mb-3">
          {(settings.recipient_emails || []).map(email => (
            <span key={email} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 rounded-full text-sm text-taxera-charcoal" data-testid={`recipient-${email}`}>
              <Mail className="w-3 h-3 text-slate-400" />
              {email}
              <button onClick={() => removeEmail(email)} className="ml-1 text-slate-400 hover:text-red-500"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input type="email" value={emailInput} onChange={e => setEmailInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addEmail()}
            placeholder="Add email address..."
            className="flex-1 border border-slate-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-taxera-teal"
            data-testid="add-email-input" />
          <button onClick={addEmail} className="px-4 py-2 taxera-gradient text-white rounded-xl text-sm font-semibold" data-testid="add-email-btn">Add</button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="text-sm font-bold text-taxera-charcoal mb-2">Event Notifications</h3>
        <ToggleRow label="New Lead" desc="SAP assessment, whitepaper downloads" settingKey="notify_new_lead" />
        <ToggleRow label="Whitepaper Download" desc="Gated whitepaper downloads" settingKey="notify_whitepaper_download" />
        <ToggleRow label="Contact Form" desc="New contact form submissions" settingKey="notify_contact_form" />
        <ToggleRow label="New Signup" desc="New user registrations" settingKey="notify_new_signup" />
        <ToggleRow label="Traffic Spike" desc={`Alert when views exceed ${settings.spike_threshold || 50}/hour`} settingKey="notify_traffic_spike" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <h3 className="text-sm font-bold text-taxera-charcoal mb-2">Traffic Spike Threshold</h3>
        <p className="text-xs text-slate-400 mb-3">Number of page views per hour that triggers an alert.</p>
        <input type="number" value={settings.spike_threshold || 50} min={10} max={1000}
          onChange={e => save({ spike_threshold: parseInt(e.target.value) || 50 })}
          className="w-32 border border-slate-200 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-taxera-teal"
          data-testid="spike-threshold" />
      </div>

      {history.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h3 className="text-sm font-bold text-taxera-charcoal">Recent Notification History</h3>
          </div>
          <div className="divide-y divide-slate-50 max-h-80 overflow-y-auto">
            {history.map((n, i) => (
              <div key={n.timestamp || i} className="px-5 py-3 flex items-center gap-3" data-testid={`notif-history-${i}`}>
                <div className={`w-2 h-2 rounded-full shrink-0 ${n.status === 'sent' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-taxera-charcoal truncate">{n.subject}</div>
                  <div className="text-2xs text-slate-400">{n.recipients?.join(', ')} — {new Date(n.timestamp).toLocaleString()}</div>
                </div>
                <span className={`text-2xs px-2 py-0.5 rounded-full font-semibold ${n.status === 'sent' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                  {n.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsTab;
